import './submission-detail-header.scss';

import { Box, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ConfirmDialog,
  NewFromSelectField,
  NewTypeAhead,
  PaperLayout,
  PrimaryTitle,
} from '../../presentation';
import {
  RENEWAL_TYPES_OPTIONS,
  getUserOptions,
} from './underwriting-queue-grid';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  SubmissionRenewalType,
  SubmissionStatus,
  SubmissionSubStatus,
  SubmissionType,
  UserGroup,
  useGetUsers,
  useUpsertSubmissionMutation,
  useUpsertSubmissionUserMutation,
} from '@converge/graphql';
import { asCase, capitalizeFirstLetter, daysAgo } from '@converge/utils';

import { SubmissionRenewalModal } from './submission-renewal-modal';
import { SubmissionStatusBadge } from './submission-status-badge';
import { isApolloError } from '@apollo/client';
import { useConvergeSnackBar } from '../../contexts';
import { useTranslation } from 'react-i18next';
import {
  useUnderwritingSubmissionActionsContext,
  useUnderwritingSubmissionDispatchContext,
  useUnderwritingSubmissionStateContext,
} from '../underwriting/underwriting.submission.context';

interface IProps {
  submission: {
    // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
    // nulls:
    submissionReferenceId?: string | null;
    submissionStatus?: SubmissionStatus | null | undefined;
    subStatus?: SubmissionSubStatus | null | undefined;
    insuredName?: string | null;
    underwriterName?: string | null;
    underwriterUuid?: string | null;
    underwriterAssistantUuid?: string | null;
    insuredLogo?: string | null;
    fallbackInsuredLogo?: string;
    brokerName?: string | null;
    dueDate?: string | null;
    uuid: string;
    createdAt: string;
    type?: SubmissionType | null;
    renewal?: boolean | null;
    renewalType?: SubmissionRenewalType | null;
    policyStartDate?: string | null;
    policyEndDate?: string | null;
  };
}

export const UnderwritingSubmissionHeader = ({ submission }: IProps) => {
  const { t } = useTranslation();
  const formMethods = useForm({});
  const {
    info: { hasQuotePackageBeenCreated },
  } = useUnderwritingSubmissionStateContext();
  const renewalTypeOptions =
    hasQuotePackageBeenCreated &&
    submission.renewalType !== SubmissionRenewalType.AutoQuoteRenewal
      ? RENEWAL_TYPES_OPTIONS.filter(
          (o) => o.value !== SubmissionRenewalType.AutoQuoteRenewal
        )
      : RENEWAL_TYPES_OPTIONS;

  const { data: underwriterList } = useGetUsers({
    userGroups: [UserGroup.Underwriter],
  });
  const { data: underwriterAssistantList } = useGetUsers({
    userGroups: [UserGroup.UnderwriterAssistant],
  });
  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList) || [],
    [underwriterList]
  );
  const UNDERWRITER_ASSISTANT_OPTIONS = useMemo(
    () => getUserOptions(underwriterAssistantList) || [],
    [underwriterAssistantList]
  );
  const { notify } = useConvergeSnackBar();
  const dispatch = useUnderwritingSubmissionDispatchContext();
  const actions = useUnderwritingSubmissionActionsContext();
  const { submission: submissionState } =
    useUnderwritingSubmissionStateContext();

  const { upsertSubmissionUser } = useUpsertSubmissionUserMutation();
  const { upsertSubmission } = useUpsertSubmissionMutation();
  const { upsertSubmission: upsertSubmissionNoAwait } =
    useUpsertSubmissionMutation({}, undefined, false);
  const updateSubmissionUser = useCallback(
    async (
      submissionUuid: string,
      userUuid: string | null,
      userGroup: UserGroup
    ) => {
      const userType =
        userGroup === UserGroup.Underwriter ? 'underwriter' : 'assistant';
      try {
        // convergeLogger.info(`${actionVerb}ing submission...`, uuid);
        notify({
          message: `Updating ${userType}...`,
          variant: 'info',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });
        await upsertSubmissionUser({
          variables: {
            submissionUuid,
            userUuid: userUuid,
            userGroup,
          },
        });
        notify({
          message: `${capitalizeFirstLetter(userType)} saved successfully`,
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });
      } catch (cause) {
        notify({
          message: `Failed to update assistant: ${
            cause instanceof Error && isApolloError(cause)
              ? cause.message
              : cause
          }`,
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        });
      }
    },
    [notify, upsertSubmissionUser]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAutoQuoteConfirmOpen, setIsAutoQuoteConfirmOpen] = useState(false);
  const [valueOfRenewal, setValueOfRenewal] = useState<
    SubmissionRenewalType | undefined
  >();

  const handleRenewalModal = (event: SelectChangeEvent<unknown>) => {
    const renewalType = event?.target?.value as SubmissionRenewalType;
    setValueOfRenewal(renewalType);
    if (renewalType === SubmissionRenewalType.AutoQuoteRenewal) {
      setIsAutoQuoteConfirmOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const callHandleUpdateRenewalType = () => {
    if (valueOfRenewal !== undefined) {
      handleUpdateRenewalType(valueOfRenewal);
    }
  };

  const handleUpdateRenewalType = async (
    renewalType: SubmissionRenewalType
  ) => {
    const isAutoQuote = renewalType === SubmissionRenewalType.AutoQuoteRenewal;
    try {
      notify({
        message: isAutoQuote
          ? 'Running auto quote...'
          : 'Updating renewal type...',
        variant: 'info',
      });
      await (isAutoQuote ? upsertSubmissionNoAwait : upsertSubmission)({
        variables: {
          details: {
            uuid: submission.uuid ?? '',
            renewalTypeOverride: renewalType,
          },
        },
      });

      if (isAutoQuote) {
        dispatch(
          actions.updateSubmission({
            ...submissionState,
            renewalType: SubmissionRenewalType.AutoQuoteRenewal,
          })
        );
      }

      notify({
        message: isAutoQuote
          ? 'Auto quote initiated successfully'
          : 'Renewal type updated successfully',
        variant: 'success',
      });
    } catch (cause) {
      notify({
        message: isAutoQuote
          ? `Failed to run auto quote: ${
              cause instanceof Error && isApolloError(cause)
                ? cause.message
                : cause
            }`
          : `Failed to update renewal type ${
              cause instanceof Error && isApolloError(cause)
                ? cause.message
                : cause
            }`,
        variant: 'error',
      });
    }
  };

  const getPolicyDates = () => {
    const policyDates = `${submission?.policyStartDate?.split('T')[0]} /
      ${submission?.policyEndDate?.split('T')[0]}`;

    const transformDate = (date: string): string => {
      const [year, month, day] = date.trim().split('-');
      return `${month}/${day}/${year}`;
    };

    const cleanedDateRange = policyDates.replace(/\s+/g, ' ').trim();
    const [startDate, endDate] = cleanedDateRange
      .split(' / ')
      .map(transformDate);

    const formattedDateRange = `${startDate} - ${endDate}`;

    return formattedDateRange;
  };
  return (
    <PaperLayout className="submission-detail-header">
      <FormProvider {...formMethods}>
        <Stack
          className="header-wrapper"
          flexDirection={'row'}
          alignItems={'center'}
          flexGrow={1}
        >
          <PrimaryTitle variant="h3" className="submission-reference">
            <span className="reference">
              {submission.submissionReferenceId}{' '}
            </span>
            {submission.submissionStatus && (
              <div className="status-wrapper">
                <SubmissionStatusBadge
                  status={submission.submissionStatus}
                  subStatus={submission.subStatus}
                  uuid={submission.uuid}
                  createdAt={submission.createdAt}
                />
              </div>
            )}
          </PrimaryTitle>
          <Stack
            gap={4}
            marginLeft={1}
            justifyContent={'center'}
            alignItems={'top'}
            flexDirection={'row'}
            sx={{
              '@media (max-width: 600px)': {
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
              },
            }}
          >
            <Stack
              className="uw-submission-logo"
              justifyContent={'center'}
              alignItems={'center'}
              direction={'row'}
            >
              {submission.insuredLogo ? (
                <figure>
                  <img
                    src={submission.insuredLogo}
                    alt={submission.insuredName ?? 'Logo'}
                    loading="lazy"
                    onError={(e) => {
                      const parent = (e.target as HTMLImageElement)
                        .parentElement;
                      if (submission.fallbackInsuredLogo) {
                        (e.target as HTMLImageElement).src =
                          submission.fallbackInsuredLogo;
                        (e.target as HTMLImageElement).style.display = 'block';
                        if (parent) {
                          parent.style.display = 'block';
                        }
                        return e;
                      } else {
                        (e.target as HTMLImageElement).style.display = 'none';

                        if (parent) {
                          parent.style.display = 'none';
                        }
                      }
                      return e;
                    }}
                  />
                </figure>
              ) : (
                ''
              )}

              <HeaderField
                label={t('underwriting.detail.header.insuredLabel', 'INSURED')}
                value={submission.insuredName ?? 'N/A'}
                maxWidth
                className="insured-name"
              />
            </Stack>
            <HeaderField
              label="BROKERAGE"
              value={submission.brokerName ?? 'N/A'}
              className="broker-name"
            />
            <HeaderField
              label={t('underwriting.detail.header.policyType', 'POLICY')}
              value={
                submission.type ? asCase(submission.type, 'sentence') : 'N/A'
              }
              subTitle={getPolicyDates()}
            />

            {submission.renewal && (
              <SelectFieldWrapper label="RENEWAL">
                <NewFromSelectField
                  disabled={
                    submission.submissionStatus !== null &&
                    submission.submissionStatus !== undefined &&
                    submission.submissionStatus ===
                      SubmissionStatus.PolicyIssued
                  }
                  name=""
                  options={renewalTypeOptions}
                  value={submission.renewalType}
                  onChange={handleRenewalModal}
                />
              </SelectFieldWrapper>
            )}
            {isModalOpen && (
              <SubmissionRenewalModal
                onClose={() => setIsModalOpen(false)}
                title=""
                uuid={submission.uuid}
                callHandleUpdateRenewalType={callHandleUpdateRenewalType}
              />
            )}
            <ConfirmDialog
              show={isAutoQuoteConfirmOpen}
              title="Auto Quote"
              content="Are you sure you want to run auto quote? A quote package will be generated for review."
              confirmButtonText="Confirm"
              cancelButtonText="Cancel"
              onConfirm={() => {
                setIsAutoQuoteConfirmOpen(false);
                handleUpdateRenewalType(SubmissionRenewalType.AutoQuoteRenewal);
              }}
              onCancel={() => setIsAutoQuoteConfirmOpen(false)}
            />
            <SelectFieldWrapper label="UW ASSISTANT">
              <Box sx={{ width: '140px' }}>
                <NewTypeAhead
                  name=""
                  options={UNDERWRITER_ASSISTANT_OPTIONS}
                  value={submission.underwriterAssistantUuid ?? 'N/A'}
                  placeholder="Select UA"
                  onChangeOption={(event) => {
                    const userUuid =
                      event?.value === '' || !event?.value ? null : event.value;

                    updateSubmissionUser(
                      submission.uuid,
                      userUuid,
                      UserGroup.UnderwriterAssistant
                    );
                  }}
                />
              </Box>
            </SelectFieldWrapper>

            <SelectFieldWrapper label="UNDERWRITER">
              <Box sx={{ width: '140px' }}>
                <NewTypeAhead
                  name=""
                  placeholder="Select UW"
                  options={UNDERWRITER_OPTIONS}
                  value={submission.underwriterUuid ?? 'N/A'}
                  onChangeOption={(event) => {
                    const userUuid =
                      event?.value === '' || !event?.value ? null : event.value;
                    updateSubmissionUser(
                      submission.uuid,
                      userUuid,
                      UserGroup.Underwriter
                    );
                  }}
                />
              </Box>
            </SelectFieldWrapper>

            {submission.dueDate ? (
              <HeaderField
                label={t('underwriting.detail.header.due', 'DUE')}
                value={daysAgo(submission.dueDate, 'overdue') as string}
              />
            ) : (
              ''
            )}
          </Stack>
        </Stack>
      </FormProvider>
    </PaperLayout>
  );
};

const HeaderField = ({
  label,
  value,
  className,
  subTitle,
  maxWidth,
}: {
  label: string;
  value?: string;
  className?: string;
  subTitle?: string;
  maxWidth?: boolean;
}) => {
  return (
    <Stack
      gap={0.5}
      flexDirection={'column'}
      justifyContent={'start'}
      alignItems="flex-start"
      height={'100%'}
      sx={{ maxWidth: maxWidth ? '200px' : 'auto' }}
    >
      <Box>
        <Typography
          className="header-title"
          variant="body2"
          margin={0}
          lineHeight={'1em'}
        >
          {label}
        </Typography>
      </Box>
      <Box className={className ?? `${className} title-wrapper`}>
        <PrimaryTitle
          sx={{ width: maxWidth ? '180px' : 'auto' }}
          variant="h5"
          margin={0}
          lineHeight={'1em'}
        >
          {value}
        </PrimaryTitle>

        <Typography
          variant="body2"
          margin={0}
          lineHeight={'1em'}
          style={{ marginTop: '4px', fontWeight: 700, fontSize: '14px' }}
        >
          {subTitle}
        </Typography>
      </Box>
    </Stack>
  );
};

const SelectFieldWrapper = ({
  label,
  children,
}: {
  label: string;
  children?: ReactNode;
}) => {
  return (
    <Stack
      gap={0.5}
      flexDirection={'column'}
      justifyContent={'start'}
      alignItems="flex-start"
      height={'100%'}
    >
      <Box>
        <Typography
          className="select-wrapper-label"
          variant="body2"
          margin={0}
          lineHeight={'1em'}
        >
          {label}
        </Typography>
      </Box>
      {children}
    </Stack>
  );
};
